/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

/**
 * Value provenance — R4/R4b of `docs/SOURCE_DISCOVERY_DESIGN_REVIEW.md`, and `L3` of
 * `docs/ARTIFACT_REVIEW_LIFECYCLE_DESIGN.md`.
 *
 * **Two questions, one vocabulary.** They are answered by different systems at
 * different times and can legitimately disagree on the same row, so they get two
 * types rather than one:
 *
 * - {@link ValueOrigin} — *how did this row come to be?* A property of the
 *   authored artifact, known at author time, persisted with the row.
 * - {@link RuntimeValueSource} — *who supplied this value on **this** request?* A
 *   property of one request, computed on the built value.
 *
 * `runtime` appears in the authoring vocabulary while naming a runtime fact, and
 * that is deliberate rather than the conflation R4b untangles: it is the row saying
 * *"I carry no value; a request will supply one"*, which is a fact about the row.
 * What the value turns out to **be** is {@link RuntimeValueSource}'s answer.
 *
 * Both are data. Neither is derived in the browser — R4's third consequence ("a row
 * that says who set it cannot be quietly overwritten by a second storage path
 * without the chip contradicting itself") is a correctness claim, and a
 * console-derived label cannot satisfy it: it derives the same wrong answer from the
 * same wrong data, which is how #891 read correct while being wrong.
 *
 * **Aliased into the console, not mirrored** (`@value-provenance`, `#1001`). The stored shape has a
 * legacy spelling and a rule for when a guess stops being one, and both are read by the admin
 * service on save and by every console store boundary on load. A mirrored copy of either is a
 * second reader of one stored shape, which is `#1269`'s defect waiting for its first disagreement.
 */

import type { SampleLiteralOutcome } from './sample-literal-policy';

/**
 * By what mechanism an authored value arrived. Mirrors `BADGE_PROVENANCE` in the design
 * system (`.claude/skills/bidbrain-design/components/core/Badge.jsx`) exactly.
 *
 * **There is deliberately no `user` member.** An unchipped value is plainly
 * user-set, and chipping everything makes the chips invisible — the failure mode a
 * provenance system dies of. `overridden` is the useful fifth value because it is
 * the one state a user cannot infer from absence.
 *
 * Extend this only by extending `BADGE_PROVENANCE` first, or the surfaces stop
 * agreeing about what a state means. Adding a **dimension** to {@link ValueOrigin} touches
 * neither (`L3`).
 */
export type ValueProvenance = 'detected' | 'template' | 'inherited' | 'runtime' | 'overridden';

export const VALUE_PROVENANCE_VALUES: readonly ValueProvenance[] = [
  'detected',
  'template',
  'inherited',
  'runtime',
  'overridden',
] as const;

export function isValueProvenance(value: unknown): value is ValueProvenance {
  return typeof value === 'string' && (VALUE_PROVENANCE_VALUES as readonly string[]).includes(value);
}

/**
 * **How much a detector believed the mapping it proposed** (`#1001`).
 *
 * Every detector already grades — `suggestMapping` returns this, `matchMacroName` returns `exact` —
 * and until `#1001` every row builder threw the grade away, so a mapping matched on one word of a
 * compound name rendered exactly like an exact match. `high` is an exact name; `medium` is a whole
 * token or run of a longer name; `low` is weaker evidence than either.
 */
export type DetectionConfidence = 'high' | 'medium' | 'low';

const DETECTION_CONFIDENCE_VALUES: readonly DetectionConfidence[] = ['high', 'medium', 'low'];

/**
 * **What the sample carried at a detected mapping — what Revert goes back to** (`#1001`).
 *
 * Either the literal itself, or the reason it was not kept. The second is not an absence: a
 * viewer's IP or a consent string is never stored (`#952`, `#1188`), and a row that only lacked a
 * sample could not say *why* there is nothing to revert to — which is the sentence the operator
 * needs, because the obvious reading of a missing Revert is that the feature is broken.
 */
export type SampleLiteral =
  | { readonly value: string | number | boolean }
  | { readonly withheld: Exclude<SampleLiteralOutcome, 'retain'> };

/**
 * **Where a value came from — the record `L3` ratified**, sized by `#1001`'s audit.
 *
 * `L3` promoted the bare provenance string to a record so a value could carry more than its
 * mechanism. It named `actor`, `at` and `correlationId` for multi-editor deployments; **none of those
 * has a writer yet, so none is declared** — a field with no writer is the `#1017` family. What
 * `#1001` needed is the two below, and only on `detected`: the audit found every guess in the
 * product arrives through a detector, so there is no new `kind`. An epistemic member (*a guess
 * nobody confirmed*) waits for its first consumer that is not `detected` (`#1019`).
 */
/**
 * **How well a vendor template's claims about a field are known** (`D40`, ratified 2026-08-28, built
 * in `1f-iii`).
 *
 * | Grade | Means |
 * | --- | --- |
 * | `observed` | from a real tag or live config — the repository's deployment backups are this |
 * | `inferred` | reasoned from convention or naming, not verified. Usable, and it says so |
 * | `unverified` | nothing checked. The work queue |
 *
 * It grades the **declaration** (type, description, importance), not the operator's mapping, so an
 * Apply does not clear it. No `inferred` declaration survives into pilot ramp-up (`D40`'s stage gate).
 */
export type EvidenceGrade = 'observed' | 'inferred' | 'unverified';
const EVIDENCE_GRADE_VALUES: readonly EvidenceGrade[] = ['observed', 'inferred', 'unverified'];

export interface ValueOrigin {
  readonly kind: ValueProvenance;
  /** Only on `detected`, and only where a detector proposed a mapping. Cleared once confirmed. */
  readonly confidence?: DetectionConfidence;
  /** Only on `detected`, and only where a detector proposed a mapping. Cleared once confirmed. */
  readonly sample?: SampleLiteral;
  /**
   * **Only on `template`: how well the template's claims about this field are known** (`D40`). This is
   * the epistemic member the note above waited for: its first consumer is a vendor template, not a
   * detector. It travels with the row at apply, because it is about the vendor's field rather than
   * about the source the template was made from.
   */
  readonly evidence?: EvidenceGrade;
}

/** An origin with nothing but its mechanism. */
export function originOf(kind: ValueProvenance): ValueOrigin {
  return { kind };
}

/**
 * **Read a stored origin — the one legacy read, for every store** (`X3`'s pattern).
 *
 * Until `#1001` every store held the bare kind (`'detected'`), and nothing rewrites a document
 * until it is next saved, so the string is still read. A write emits the record only.
 *
 * Validated rather than trusted: an unrecognised kind reads as absent — an unchipped row — and a
 * confidence or sample on anything but `detected` is dropped, because on any other kind it would be
 * a claim nothing produces.
 */
export function readValueOrigin(stored: unknown): ValueOrigin | undefined {
  if (isValueProvenance(stored)) return { kind: stored };
  if (!stored || typeof stored !== 'object' || Array.isArray(stored)) return undefined;

  const record = stored as { kind?: unknown; confidence?: unknown; sample?: unknown; evidence?: unknown };
  if (!isValueProvenance(record.kind)) return undefined;
  if (record.kind === 'template') {
    const evidence = (EVIDENCE_GRADE_VALUES as readonly unknown[]).includes(record.evidence)
      ? (record.evidence as EvidenceGrade)
      : undefined;
    return { kind: 'template', ...(evidence ? { evidence } : {}) };
  }
  if (record.kind !== 'detected') return { kind: record.kind };

  const confidence = (DETECTION_CONFIDENCE_VALUES as readonly unknown[]).includes(record.confidence)
    ? (record.confidence as DetectionConfidence)
    : undefined;
  const sample = readSampleLiteral(record.sample);
  return {
    kind: 'detected',
    ...(confidence ? { confidence } : {}),
    ...(sample ? { sample } : {}),
  };
}

function readSampleLiteral(stored: unknown): SampleLiteral | undefined {
  if (!stored || typeof stored !== 'object' || Array.isArray(stored)) return undefined;
  const record = stored as { value?: unknown; withheld?: unknown };
  if (record.withheld === 'discard-per-request' || record.withheld === 'discard-consent') {
    return { withheld: record.withheld };
  }
  const value = record.value;
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') return { value };
  return undefined;
}

/**
 * **A detected mapping nobody has looked at yet, and whose match was not exact** — the rows the
 * confidence mark is for (`#1001`).
 *
 * `high` is excluded on purpose: the chip already reads *we found this in your tag*, and an exact
 * name agrees with that. The mark goes on the case that contradicts it, which is the whole of
 * CLAUDE.md's *which case is the exception* rule.
 */
/**
 * **A template field whose declaration is a guess** (`D40`) — the rows the `inferred` mark is for.
 * `observed` agrees with the chip and `unverified` declares nothing to doubt, so only this case is
 * marked (CLAUDE.md's *which case is the exception*).
 */
export function isInferredDeclaration(origin: ValueOrigin | undefined): boolean {
  return origin?.kind === 'template' && origin.evidence === 'inferred';
}

/**
 * **A template origin, keeping its grade** — what a row the template seeds carries on the new source
 * (`D40`). Any other origin becomes a plain `template`: a detected guess on the source a template was
 * saved from is not a guess about **this** source.
 */
export function templateSuppliedOrigin(origin: ValueOrigin | undefined): ValueOrigin {
  return origin?.kind === 'template' && origin.evidence
    ? { kind: 'template', evidence: origin.evidence }
    : { kind: 'template' };
}

export function isUnconfirmedGuess(origin: ValueOrigin | undefined): boolean {
  return origin?.kind === 'detected' && (origin.confidence === 'medium' || origin.confidence === 'low');
}

/**
 * **What an origin becomes when the operator opens its row and applies it** (Todd, 2026-09-15).
 *
 * Opening a row and pressing Apply is the operator looking at the mapping and accepting it, so the
 * guess becomes a decision: the confidence clears and the sample Revert would have returned to is
 * dropped — a third party's literal kept for a control that no longer renders is a value with no
 * reader. The mechanism stays `detected`, because that is still how the row came to exist.
 *
 * Cancel does not come here, deliberately: leaving is not accepting.
 */
export function confirmedOrigin(origin: ValueOrigin | undefined): ValueOrigin | undefined {
  if (!origin || origin.kind !== 'detected') return origin;
  if (origin.confidence === undefined && origin.sample === undefined) return origin;
  return { kind: 'detected' };
}

/**
 * **What Revert puts back** — the literal the sample carried, where one was kept (`#1001`).
 *
 * `undefined` where there is nothing to revert to: no detector guess, a guess already confirmed, or a
 * literal a policy refused to keep. The last is the one a surface must *say* rather than merely omit
 * the control for — `describeUnconfirmedGuess` does.
 */
export function revertValueOf(origin: ValueOrigin | undefined): string | number | boolean | undefined {
  const sample = origin?.kind === 'detected' ? origin.sample : undefined;
  return sample && 'value' in sample ? sample.value : undefined;
}

/**
 * Who supplied a value on one request.
 *
 * This is the half #975's precedence rule needs — *"we win when we decided; they win
 * otherwise, and a code default is not a decision"* — which is only implementable if
 * a built value can say which it was. Today `bidfloor: 0.5` in the driver is
 * indistinguishable from an operator who configured `0.5`, and the two must behave in
 * opposite ways.
 *
 * Generalises `LayerMergeEntry.layer` (`libs/request-decorators/layer-merge.ts`),
 * which already records the contributing layer for resolved parameters.
 */
export type RuntimeValueSource =
  /** An operator mapping or static, a strategy/round-rule value, or source config. */
  | 'decided'
  /** Replayed from what the caller sent on this request. */
  | 'replayed'
  /** A literal the driver supplied because nothing else did. **Not a decision.** */
  | 'default';

/**
 * Authoring provenance for a document with no per-leaf model, keyed by dotted path
 * (`imp.0.video.mimes`).
 *
 * Required because `OrtbBodyTemplateDto.body` is `Record<string, any>` and is sent
 * **verbatim** — there is no leaf to hang an attribute on, and metadata written
 * inside the document would go on the wire. So it lives beside the body, not in it.
 *
 * The surfaces are not symmetric and a plan that assumes they are breaks here:
 * `DemandSourceParamField` is a real per-row interface and takes the attribute
 * directly.
 */
export type ProvenanceByPath = Record<string, ValueOrigin>;

/**
 * Read a side-car entry for `path`, treating an entry whose path no longer resolves
 * in `document` as **absent rather than as an error**.
 *
 * A side-car goes stale by design: the body is edited as a JSON document, so a path
 * can move or vanish underneath its provenance entry. The alternative — surfacing
 * that as a warning — produces a provenance system that reports on its own
 * bookkeeping, which operators learn to ignore, taking the real chips with it.
 *
 * An unchipped row is the correct rendering of "we no longer know", and it is also
 * the honest one.
 */
export function resolveProvenanceAtPath(
  sidecar: Record<string, unknown> | undefined,
  document: unknown,
  path: string,
): ValueOrigin | undefined {
  const origin = readValueOrigin(sidecar?.[path]);
  if (!origin) return undefined;
  return pathExists(document, path) ? origin : undefined;
}

/**
 * Drop side-car entries whose paths no longer resolve, and write the survivors in the record form.
 *
 * Called on save rather than on read: reading tolerates staleness (above), but
 * persisting it forever means the map grows without bound as an operator edits the
 * body, and a later path that happens to match a long-dead entry would inherit a
 * provenance it never had.
 */
export function pruneProvenanceByPath(
  sidecar: Record<string, unknown> | undefined,
  document: unknown,
): ProvenanceByPath {
  if (!sidecar) return {};
  const pruned: ProvenanceByPath = {};
  for (const [path, stored] of Object.entries(sidecar)) {
    const origin = readValueOrigin(stored);
    if (origin && pathExists(document, path)) {
      pruned[path] = origin;
    }
  }
  return pruned;
}

/**
 * Whether a dotted path addresses something in `document`.
 *
 * Numeric segments index arrays (`imp.0.video`). A path resolving to an explicit
 * `null` still **exists** — the operator authored that null, and treating it as
 * missing would silently drop its provenance.
 */
function pathExists(document: unknown, path: string): boolean {
  if (!path) return false;

  let current: unknown = document;
  for (const segment of path.split('.')) {
    if (current === null || current === undefined) return false;

    if (Array.isArray(current)) {
      const index = Number(segment);
      if (!Number.isInteger(index) || index < 0 || index >= current.length) return false;
      current = current[index];
      continue;
    }

    if (typeof current !== 'object') return false;
    if (!Object.prototype.hasOwnProperty.call(current, segment)) return false;
    current = (current as Record<string, unknown>)[segment];
  }

  return true;
}
